
import React, { ReactNode, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { LayoutDashboard, Users, ArrowLeftRight, Settings, LogOut, Bell, Shield, Wallet, Zap, Briefcase, BarChart3, ShieldCheck, TrendingUp, Megaphone, MessageSquare, DollarSign } from 'lucide-react';
import { cn } from '@/shared/lib/utils';
import { auth } from '@/lib/firebase';
import { signOut } from 'firebase/auth';
import { useTransactionNotifications } from '@/shared/hooks/useTransactionNotifications';
import { usePendingApprovals } from '@/shared/hooks/usePendingApprovals';

interface AdminLayoutProps {
    children: ReactNode;
}

import { ToastProvider } from '@/shared/context/ToastContext';
import { useAdminNotifications } from '@/shared/hooks/useAdminNotifications';

// Helper component to activate notifications inside the provider
const AdminNotifications = () => {
    useAdminNotifications();
    return null;
};

const AdminLayout: React.FC<AdminLayoutProps> = ({ children }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
    const { pendingCount } = useTransactionNotifications();
    const { pendingCount: pendingApprovalCount } = usePendingApprovals();

    const handleLogout = async () => {
        try {
            await signOut(auth);
        } catch (error) {
            console.error("Logout failed", error);
        } finally {
            localStorage.clear();
            sessionStorage.clear();
            window.location.href = '/login';
        }
    };

    const menuItems = [
        { icon: LayoutDashboard, label: '대시보드', path: '/kimstar/dashboard' },
        { icon: Users, label: '사용자 관리', path: '/kimstar/users' },
        { icon: Zap, label: '데이트레이딩 관리', path: '/kimstar/daytrading' },
        { icon: Briefcase, label: '블록딜 관리', path: '/kimstar/blockdeal' },
        { icon: BarChart3, label: 'ETF 관리', path: '/kimstar/etf' },
        { icon: TrendingUp, label: '스테이킹 관리', path: '/kimstar/staking' },
        { icon: Megaphone, label: '공지사항 관리', path: '/kimstar/notices' },
        { icon: MessageSquare, label: '고객지원 관리', path: '/kimstar/support' },
        { icon: ShieldCheck, label: '본인인증 관리', path: '/kimstar/verification' },
        { icon: ArrowLeftRight, label: 'P2P 거래 관리', path: '/kimstar/p2p' },
        { icon: DollarSign, label: '입출금 신청 관리', path: '/kimstar/transaction-requests' },
        { icon: ArrowLeftRight, label: '거래 내역 조회', path: '/kimstar/transactions' },
        { icon: Settings, label: '시스템 설정', path: '/kimstar/settings' },
    ];

    return (
        <ToastProvider>
            <AdminNotifications />
            <div className="min-h-screen bg-slate-50 text-slate-900 flex admin-light">
                {/* Admin Sidebar */}
                <aside className="w-64 bg-white border-r border-slate-200 flex flex-col fixed inset-y-0 z-50">
                    <div className="p-6 flex justify-center">
                        <img src="/로고.jpg" alt="Coin2Cash" className="w-20 h-20 object-contain" />
                    </div>

                    <nav className="flex-1 px-4 py-6 space-y-1">
                        {menuItems.map((item) => (
                            <button
                                key={item.path}
                                onClick={() => navigate(item.path)}
                                className={cn(
                                    "w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 group text-sm font-bold",
                                    location.pathname.startsWith(item.path)
                                        ? "bg-primary text-white shadow-lg shadow-blue-500/30"
                                        : "text-slate-500 hover:bg-slate-100 hover:text-slate-900"
                                )}
                            >
                                <item.icon className="h-5 w-5" />
                                <span className="flex-1 text-left">{item.label}</span>
                                {item.path === '/kimstar/users' && pendingApprovalCount > 0 && (
                                    <span className="h-5 w-5 bg-red-500 text-white text-[10px] font-black rounded-full flex items-center justify-center">
                                        {pendingApprovalCount > 9 ? '9+' : pendingApprovalCount}
                                    </span>
                                )}
                                {item.path === '/kimstar/transaction-requests' && pendingCount > 0 && (
                                    <span className="h-5 w-5 bg-red-500 text-white text-[10px] font-black rounded-full flex items-center justify-center">
                                        {pendingCount > 9 ? '9+' : pendingCount}
                                    </span>
                                )}
                            </button>
                        ))}
                    </nav>

                    <div className="p-4 border-t border-slate-200 mt-auto">
                        <button className="w-full h-12 flex items-center gap-3 px-4 rounded-xl text-slate-500 hover:bg-red-50 hover:text-red-600 transition-colors text-sm font-bold">
                            <LogOut className="h-5 w-5" />
                            <span>로그아웃</span>
                        </button>
                    </div>
                </aside>

                {/* Main Admin Area */}
                <div className="flex-1 ml-64 min-h-screen flex flex-col bg-slate-50">
                    <header className="h-16 border-b border-slate-200 flex items-center justify-between px-8 bg-white/80 backdrop-blur-md sticky top-0 z-40">
                        <div className="flex items-center gap-4">
                            <h2 className="text-sm font-bold text-slate-500 flex items-center gap-2">
                                <Shield className="h-4 w-4" /> 관리자 시스템
                            </h2>
                        </div>
                        <div className="flex items-center gap-6">
                            <button
                                onClick={() => navigate('/kimstar/transaction-requests')}
                                className="relative text-slate-400 hover:text-slate-600 transition-colors"
                            >
                                <Bell className="h-5 w-5" />
                                {pendingCount > 0 && (
                                    <span className="absolute -top-1 -right-1 h-4 w-4 bg-red-500 text-white text-[10px] font-black rounded-full flex items-center justify-center">
                                        {pendingCount > 9 ? '9+' : pendingCount}
                                    </span>
                                )}
                            </button>
                            <div className="relative">
                                <button
                                    onClick={() => setIsProfileMenuOpen(!isProfileMenuOpen)}
                                    className="flex items-center gap-3 pl-6 border-l border-slate-200 hover:bg-slate-50 p-2 rounded-lg transition-colors"
                                >
                                    <div className="text-right hidden sm:block">
                                        <p className="text-xs font-bold text-slate-900 leading-none">Kim Admin</p>
                                        <span className="text-[10px] text-slate-500 font-medium">최고 관리자</span>
                                    </div>
                                    <div className="w-8 h-8 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center">
                                        <span className="text-xs font-bold text-slate-500">A</span>
                                    </div>
                                </button>

                                {isProfileMenuOpen && (
                                    <div className="absolute right-0 top-full mt-2 w-48 bg-white border border-slate-200 rounded-xl shadow-xl overflow-hidden z-50">
                                        <div className="p-2">
                                            <button
                                                onClick={handleLogout}
                                                className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-500 hover:bg-red-50 hover:text-red-600 rounded-lg transition-colors"
                                            >
                                                <LogOut className="h-4 w-4" />
                                                <span>로그아웃</span>
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </header>

                    <main className="p-8 flex-1 overflow-auto">
                        {children}
                    </main>
                </div>
            </div>
        </ToastProvider>
    );
};

export default AdminLayout;
